import { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import {
  Chip,
  ColorsV2,
  Flex,
  Table,
  TableContainer,
  Text,
  spaces,
} from '@electricjs/arc';

import ApplicationIcon from '@/components/Applications/ApplicationIcon';
import TableCellFlexContainer from 'common/Table/TableCellFlexContainer';
import { AutomationStatusChip } from './AutomationStatusChip';
import { ManagedAppRow } from './types';

type ManagedAppsTableProps = {
  rows: ManagedAppRow[];
  tableId: string;
  sectionHeader: string;
  automationColumnHeader: string;
  applicationColumnHeader: string;
  groupsColumnHeader: string;
};

const columnHelper = createColumnHelper<ManagedAppRow>();

const ManagedAppsTable = ({
  rows,
  tableId,
  sectionHeader,
  automationColumnHeader,
  applicationColumnHeader,
  groupsColumnHeader,
}: ManagedAppsTableProps) => {
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'application',
        header: applicationColumnHeader,
        cell: ({ row }) => (
          <TableCellFlexContainer>
            <Flex vAlignContent="center" gap="1rem">
              <ApplicationIcon
                name={row.original.appName}
                icon={row.original.appIcon}
                size="3rem"
              />
              <Text variant="body-2">{row.original.appName}</Text>
            </Flex>
          </TableCellFlexContainer>
        ),
        meta: { flex: '3' },
      }),
      columnHelper.display({
        id: 'automationStatus',
        header: automationColumnHeader,
        cell: ({ row }) => (
          <TableCellFlexContainer>
            <AutomationStatusChip
              id={`automation-status-${row.original.appId}`}
              isAutomated={row.original.isAutomated}
            />
          </TableCellFlexContainer>
        ),
        meta: { flex: '2' },
      }),
      columnHelper.display({
        id: 'groups',
        header: groupsColumnHeader,
        cell: ({ row }) => (
          <TableCellFlexContainer gap="0.5rem" flexWrap="wrap">
            {row.original.groups.map(group => (
              <Chip
                key={group.id}
                id={`managed-group-chip-${row.original.appId}-${group.id}`}
                variant="small"
                backgroundColor={ColorsV2.GRAY_LIGHTEST}>
                <Text variant="legal">{group.name}</Text>
              </Chip>
            ))}
          </TableCellFlexContainer>
        ),
        meta: { flex: '3' },
      }),
    ],
    [applicationColumnHeader, automationColumnHeader, groupsColumnHeader]
  );

  return (
    <TableContainer flexDirection="column" width="100%">
      <Flex px="2rem" py={spaces.space_m_rem}>
        <Text variant="heading-3">{sectionHeader}</Text>
      </Flex>
      <Table
        id={tableId}
        columns={columns}
        data={rows}
        loading={false}
        emptyStateText=""
      />
    </TableContainer>
  );
};

export { ManagedAppsTable };
